import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Button from "components/Button";
import BffService from "services/bff";
import { useGeneralProvider } from "contexts/general";
import {
  Container,
  Column,
  Title,
  List,
  Row,
  RowInfo,
  Name,
  Meta,
  Actions,
  SmallButton,
  Footer,
  EmptyState,
} from "./styles";

function CompanyForms({ companyId, isEditingCustomer }) {
  const { globalUser, setLoading } = useGeneralProvider();
  const bffService = useMemo(() => new BffService(globalUser), [globalUser]);
  const [allForms, setAllForms] = useState([]);
  const [assignedForms, setAssignedForms] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      bffService.getForms({ page: 1, limit: 500 }),
      bffService.getCompanyForms(companyId),
    ])
      .then(([formsResponse, companyFormsResponse]) => {
        setAllForms(formsResponse?.data?.data || []);
        setAssignedForms(companyFormsResponse?.data || []);
      })
      .catch(() => {
        toast.error("Falha ao carregar formulários da empresa");
      })
      .finally(() => setLoading(false));
  }, [bffService, companyId, setLoading]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const availableForms = useMemo(() => {
    const assignedSlugs = new Set(assignedForms.map((item) => item.formTemplateSlug));
    return allForms.filter((item) => item.isCurrent && !assignedSlugs.has(item.slug));
  }, [allForms, assignedForms]);

  const assignForm = (item) => {
    setAssignedForms([
      ...assignedForms,
      {
        companyId,
        formTemplateSlug: item.slug,
        formName: item.name,
        isActive: true,
        order: assignedForms.length,
      },
    ]);
  };

  const removeForm = (slug) => {
    setAssignedForms(assignedForms.filter((item) => item.formTemplateSlug !== slug));
  };

  const moveForm = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= assignedForms.length) {
      return;
    }

    const nextAssigned = [...assignedForms];
    const [removedItem] = nextAssigned.splice(index, 1);
    nextAssigned.splice(nextIndex, 0, removedItem);
    setAssignedForms(nextAssigned);
  };

  const toggleActive = (index) => {
    const nextAssigned = [...assignedForms];
    nextAssigned[index] = {
      ...nextAssigned[index],
      isActive: !nextAssigned[index].isActive,
    };
    setAssignedForms(nextAssigned);
  };

  const handleSave = () => {
    setSaving(true);
    bffService
      .updateCompanyForms(
        companyId,
        assignedForms.map((item, index) => ({
          formTemplateSlug: item.formTemplateSlug,
          isActive: item.isActive,
          order: index,
        }))
      )
      .then((response) => {
        setAssignedForms(response?.data || []);
        toast.success("Formulários da empresa atualizados com sucesso!");
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || "Falha ao salvar formulários da empresa");
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <Container>
        <Column>
          <Title>Disponíveis</Title>
          <List>
            {availableForms.length > 0 ? (
              availableForms.map((item) => (
                <Row key={item.slug}>
                  <RowInfo>
                    <Name>{item.name}</Name>
                    <Meta>{item.version}</Meta>
                  </RowInfo>
                  <Actions>
                    <SmallButton disabled={!isEditingCustomer} onClick={() => assignForm(item)}>
                      +
                    </SmallButton>
                  </Actions>
                </Row>
              ))
            ) : (
              <EmptyState>Não há formulários ativos disponíveis.</EmptyState>
            )}
          </List>
        </Column>

        <Column>
          <Title>Atribuídos</Title>
          <List>
            {assignedForms.length > 0 ? (
              assignedForms.map((item, index) => (
                <Row key={item.formTemplateSlug}>
                  <RowInfo>
                    <Name>{item.formName}</Name>
                    <Meta>{item.isActive ? "Ativo" : "Inativo"}</Meta>
                  </RowInfo>
                  <Actions>
                    <SmallButton disabled={!isEditingCustomer || index === 0} onClick={() => moveForm(index, -1)}>
                      ↑
                    </SmallButton>
                    <SmallButton
                      disabled={!isEditingCustomer || index === assignedForms.length - 1}
                      onClick={() => moveForm(index, 1)}
                    >
                      ↓
                    </SmallButton>
                    <SmallButton disabled={!isEditingCustomer} onClick={() => toggleActive(index)}>
                      {item.isActive ? "Desativar" : "Ativar"}
                    </SmallButton>
                    <SmallButton disabled={!isEditingCustomer} onClick={() => removeForm(item.formTemplateSlug)}>
                      ×
                    </SmallButton>
                  </Actions>
                </Row>
              ))
            ) : (
              <EmptyState>Nenhum formulário atribuído.</EmptyState>
            )}
          </List>
        </Column>
      </Container>

      {isEditingCustomer ? (
        <Footer>
          <Button label={saving ? "Salvando..." : "Salvar"} onClick={handleSave} disabled={saving} />
        </Footer>
      ) : null}
    </>
  );
}

export default CompanyForms;
